import { CdkMenuTrigger } from '@angular/cdk/menu';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { DatePipe, Time } from '@angular/common';
import { AfterContentChecked, ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, TemplateRef, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateRange, MatCalendarView } from '@angular/material/datepicker';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subscription } from 'rxjs';

@Component({
    selector: 'is-date-time-picker',
    templateUrl: './is-date-time-picker.component.html',
    styleUrls: ['./is-date-time-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ISDateTimePickerComponent),
            multi: true
        }
    ]
})
export class ISDateTimePickerComponent implements OnInit, ControlValueAccessor, OnDestroy, OnChanges {
    @Input() Type: DatePickerType = DatePickerType.none; // Used to store the type of the date picker
    @Input() selectedRangeValue: any;
    @Input() formFloatingLayout: boolean = false;
    @Input() clearDatesBtn: boolean = true;
    @Input() placeholder: string | null = null;
    @Input() label: string | null = null;
    @Input() required: boolean = false;
    @Input() dateFormat: string = 'dd/MM/yyyy';
    @Input() dateLimits: any[] = [null, null]; // Guarda os limites do datepicker onde uma data fora dos limites não poderá ser selecionada (dateLimits[0] = Limite inferior, dateLimits[1] = Limite Superior)

    @Input() smallInput: boolean = false;
    @Input() disabled: boolean = false; // Used to set the disabled state of the input
    @Input() solid: boolean = true;
    @Input() valid: boolean = false;
    @Input() invalid: boolean = false;
    @Input() separateInputs: boolean = false;

    @Output() onSelect: EventEmitter<any> = new EventEmitter<any>();
    @Output() onDateClean: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild(CdkMenuTrigger) datePickerTheme: CdkMenuTrigger | undefined;

    isOpen = false;
    affectedDate: 'start' | 'end' | null = null;
    startView: MatCalendarView = 'month';

    dateNow = new Date();
    form = new FormGroup({
        date: new FormControl<string | null>(null),
        startDate: new FormControl<string | null>(null),
        endDate: new FormControl<string | null>(null),
        time: new FormControl<string>(this.dateNow.getHours() + ':' + this.dateNow.getMinutes()),
        startTime: new FormControl<string>(this.dateNow.getHours() + ':' + this.dateNow.getMinutes()),
        endTime: new FormControl<string>(this.dateNow.getHours() + ':' + this.dateNow.getMinutes()),
    });

    // Guarda o valor do range anterior (para efeitos de ao cancelar restorar a seleção anterior)
    previousSelectedRangeValue: any;

    // Função a chamar para passar o ngValue para o formControl no componente pai
    onChange = (value: any) => { };
    onTouched: any = () => { };

    confirmBtnDisabled = true; // Usado para dar disable ao botão de confirmação

    // Guarda o enum numa variável que possa ser acedida pelo HTML
    _DatePickerType = DatePickerType;
    _PresetDate = PresetDateRanges;

    // Vetor com as subscrições ativas no componente
    subscriptions: Subscription[] = [];

    overlayPosition: ConnectedPosition[] = [{
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top'
    },
    {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom'
    }];

    menuPosition: ConnectedPosition[] = [{
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'top'
    }];

    constructor(
        private _datePipe: DatePipe,
        private _elementRef: ElementRef
    ) { }

    ngOnInit(): void {

        if (this.Type == DatePickerType.month) {
            this.startView = 'year';
        }

        // Guarda as subscrições do valueChanges do form para depois poder limpá-las no ngOnDestroy
        this.subscriptions.push(
            // Quando os controls que guardam o tempo forem alterados automaticamente altera as horas das datas correspondentes
            // Hora
            this.form.controls.time.valueChanges.subscribe((time: string | null) => {
                // Se o input for de data vai pegar no valor do tempo e adicioná-lo à data ...
                if (this.Type != DatePickerType.time && this.Type != DatePickerType.timeRange) {
                    let splitTime = time?.split(':') ?? ['00', '00'];
                    this.selectedRangeValue?.setHours(+splitTime[0], +splitTime[1]);
                    this.form.controls.date.setValue(this.pipeDate(this.selectedRangeValue));
                }
                // ... Senão guarda o tempo e emite o evento de alteração do ngValue
                else {
                    this.selectedRangeValue = time;
                }
                this.onChange(this.selectedRangeValue);
            }),
            // Hora inicial
            this.form.controls.startTime.valueChanges.subscribe((time: string | null) => {
                if (this.Type != DatePickerType.time && this.Type != DatePickerType.timeRange) {
                    let splitTime = time?.split(':') ?? ['00', '00'];
                    this.selectedRangeValue?.start.setHours(+splitTime[0], +splitTime[1]);
                    this.form.controls.startDate.setValue(this.pipeDate(this.selectedRangeValue?.start));
                } else {
                    // Valida se o objeto com o tempo tem a propriedade {start}
                    if (Object.hasOwn(this.selectedRangeValue, 'start')) {
                        this.selectedRangeValue.start = time;
                    }
                    else {
                        this.selectedRangeValue = { start: time, end: null };
                    }
                    // Se já tiver ambas as horas de inicio e fim guardadas emite o ngValue
                    if (this.selectedRangeValue.start && this.selectedRangeValue.end) {

                        if (this.selectedRangeValue.start > this.selectedRangeValue.end) {
                            this.selectedRangeValue = { start: this.selectedRangeValue.end, end: this.selectedRangeValue.start };
                            this.detectChanges(this.selectedRangeValue);
                        }
                        this.onChange(this.selectedRangeValue);
                    }
                }
            }),
            // Hora final
            this.form.controls.endTime.valueChanges.subscribe((time: string | null) => {
                if (this.Type != DatePickerType.time && this.Type != DatePickerType.timeRange) {
                    let splitTime = time?.split(':') ?? ['00', '00'];
                    this.selectedRangeValue?.end.setHours(+splitTime[0], +splitTime[1]);
                    this.form.controls.endDate.setValue(this.pipeDate(this.selectedRangeValue?.end));
                } else {
                    // Valida se o objeto com o tempo tem a propriedade {end}
                    if (Object.hasOwn(this.selectedRangeValue, 'end')) {
                        this.selectedRangeValue.end = time;
                    }
                    else {
                        this.selectedRangeValue = { start: null, end: time };
                    }
                    // Se já tiver ambas as horas de inicio e fim guardadas emite o ngValue
                    if (this.selectedRangeValue.start && this.selectedRangeValue.end) {
                        this.onChange(this.selectedRangeValue);
                    }
                }
            })
        );

        // Inicializa o formato da data baseado no tipo do input
        switch (this.Type) {
            case DatePickerType.date:
                this.dateFormat = 'dd/MM/yyyy';
                break;
            case DatePickerType.time:
                this.dateFormat = 'HH:mm';
                break;
            case DatePickerType.dateTime:
                this.dateFormat = 'dd/MM/yyyy HH:mm';
                break;
            case DatePickerType.dateRange:
                this.dateFormat = 'dd/MM/yyyy';
                break;
            case DatePickerType.timeRange:
                this.dateFormat = 'HH:mm';
                break;
            case DatePickerType.dateTimeRange:
                this.dateFormat = 'dd/MM/yyyy HH:mm';
                break;
            default:
                this.dateFormat = 'dd/MM/yyyy';
                break;
        }

        if (this.dateLimits && this.dateLimits.length >= 2) {
            this.dateLimits.length = 2;
            if (this.dateLimits[0] && this.dateLimits[1] && this.dateLimits[0] > this.dateLimits[1]) {
                let aux = this.dateLimits[0];
                this.dateLimits[0] = this.dateLimits[1];
                this.dateLimits[1] = aux;
            }
        }
        else if (this.dateLimits.length == 1) {
            this.dateLimits.length = 2;
        }
    }

    // Tratamento especial de inputs alterados
    ngOnChanges(changes: SimpleChanges): void {
        // Se o tipo for alterado ajusta o formato da data
        if (changes['Type']) {
            switch (this.Type) {
                case DatePickerType.date:
                    this.dateFormat = 'dd/MM/yyyy';
                    break;
                case DatePickerType.time:
                    this.dateFormat = 'HH:mm';
                    break;
                case DatePickerType.dateTime:
                    this.dateFormat = 'dd/MM/yyyy HH:mm';
                    break;
                case DatePickerType.dateRange:
                    this.dateFormat = 'dd/MM/yyyy';
                    break;
                case DatePickerType.timeRange:
                    this.dateFormat = 'HH:mm';
                    break;
                case DatePickerType.dateTimeRange:
                    this.dateFormat = 'dd/MM/yyyy HH:mm';
                    break;
                default:
                    this.dateFormat = 'dd/MM/yyyy';
                    break;
            }
        }
        // Se o input do range de valores for alterado tem de aplicar as mudanças ao form
        if (changes['selectedRangeValue']) {
            this.detectChanges(changes['selectedRangeValue'].currentValue);
        }

        if (changes['dateLimits']) {
            if (this.dateLimits && this.dateLimits.length >= 2) {
                this.dateLimits.length = 2;
                if (this.dateLimits[0] && this.dateLimits[1] && this.dateLimits[0] > this.dateLimits[1]) {
                    let aux = this.dateLimits[0];
                    this.dateLimits[0] = this.dateLimits[1];
                    this.dateLimits[1] = aux;
                }
            }
            else if (this.dateLimits.length == 1) {
                this.dateLimits.length = 2;
            }
        }
    }

    /**
     * Guarda a data na variável de range e aplica caso necessário o offset à data
     * @param newDataValue Valor a guardar
     */
    selectedChange(newDataValue: any) {
        //Se for do tipo range
        if (this.Type === DatePickerType.dateTimeRange || this.Type === DatePickerType.dateRange) {
            if (this.separateInputs) {
                if (this.selectedRangeValue == null || !Object.hasOwn(this.selectedRangeValue, 'start')) {
                    this.selectedRangeValue = new DateRange<Date>(null, null);
                }
                if (this.affectedDate == 'start') {
                    this.selectedRangeValue.start = newDataValue;
                    this.form.controls.startDate.setValue(this.pipeDate(newDataValue));
                } else if (this.affectedDate == 'end') {
                    this.selectedRangeValue.end = newDataValue;
                    this.form.controls.endDate.setValue(this.pipeDate(newDataValue));
                }
                this.select();
            } else {
                if (!this.selectedRangeValue?.start || this.selectedRangeValue?.end) {
                    this.selectedRangeValue = new DateRange<Date>(newDataValue, null);
                    this.form.controls.startDate.setValue(this.pipeDate(newDataValue));
                } else {
                    const start = this.selectedRangeValue.start;
                    const end = newDataValue;
                    if (end < start) {
                        this.selectedRangeValue = new DateRange<Date>(end, start);
                    } else {
                        this.selectedRangeValue = new DateRange<Date>(start, end);
                    }
                    if (this.Type === DatePickerType.dateTimeRange) {
                        let startTime = this.form.controls.startTime.value?.split(':') ?? ['00', '00'];
                        let endTime = this.form.controls.endTime.value?.split(':') ?? ['00', '00'];
                        // Valida se o tempo tem valor e se esse valor é válido
                        if (startTime && startTime.some((time: string) => !isNaN(+time))) {
                            this.selectedRangeValue.start.setHours(startTime[0], startTime[1]);
                        }
                        if (endTime && endTime.some((time: string) => !isNaN(+time))) {
                            this.selectedRangeValue.end.setHours(endTime[0], endTime[1]);
                        }

                    }
                    else {
                        // Como estamos a selecionar um dia apenas as horas não interessa entao removemos a timezone
                        this.selectedRangeValue.start = this.offsetTimeZone(this.selectedRangeValue.start);
                        this.selectedRangeValue.end = this.offsetTimeZone(this.selectedRangeValue.end);
                    }

                    // guarda as datas formatadas no form para efeitos visuais
                    this.form.controls.startDate.setValue(this.pipeDate(this.selectedRangeValue.start));
                    this.form.controls.endDate.setValue(this.pipeDate(this.selectedRangeValue.end));
                    this.select();
                }
            }

        }
        //Se for do tipo data simples
        else if (this.Type === DatePickerType.date || this.Type === DatePickerType.dateTime) {
            if (this.Type === DatePickerType.dateTime) {
                let time = this.form.controls.time.value?.split(':');
                if (time) {
                    newDataValue.setHours(time[0], time[1]);
                }
                this.selectedRangeValue = newDataValue;
            } else {
                //Como estamos a selecionar um dia apenas as horas não interessa entao removemos a timezone
                //* Exemplo: Horário de verão -> GMT +0100
                //*          Ou seja se eu selecionar o dia 30 de agosto(hórario verão) a data irá ficar: 30/08/2023 00:00 +GMT 0100
                //*          E quando a data for convertida será aplicada a timezone(vai retirar 1 hora) e a data fica 29/08/2023
                //*          Entao visto que só queremos selecionar 1 dia em concreto temos de "contrariar" a timezone
                this.selectedRangeValue = this.offsetTimeZone(newDataValue);
            }
            this.form.controls.date.setValue(this.pipeDate(this.selectedRangeValue));
            this.select();
        }

        this.onTouched();
    }

    monthSelected(newDataValue: any) {
        if (this.Type == DatePickerType.month) {
            this.selectedRangeValue = this.offsetTimeZone(newDataValue);
            this.form.controls.date.setValue(this.pipeDate(this.selectedRangeValue, true));
            this.select();
        }
    }

    /**
     * @description Guarda as datas introduzidas e emite-as para o ngValue
     */
    select() {
        if (this.Type == DatePickerType.dateTimeRange || this.Type == DatePickerType.dateRange) {
            if (!this.separateInputs) {
                if (this.selectedRangeValue.start && this.selectedRangeValue.end) {
                    // Junta as horas às datas
                    if (this.Type === DatePickerType.dateTimeRange) {
                        let startTime = this.form.controls.startTime.value?.split(':') ?? ['00', '00'];
                        let endTime = this.form.controls.endTime.value?.split(':') ?? ['00', '00'];
                        this.selectedRangeValue.start?.setHours(+startTime[0], +startTime[1]);
                        this.selectedRangeValue.end?.setHours(+endTime[0], +endTime[1]);
                    }
                    this.onChange({ start: this.selectedRangeValue.start, end: this.selectedRangeValue.end });
                    this.onSelect.emit(this.selectedRangeValue);
                }
            } else {
                this.onChange({ start: this.selectedRangeValue.start, end: this.selectedRangeValue.end });
                this.onSelect.emit(this.selectedRangeValue);
            }
        }

        if (this.Type == DatePickerType.date || this.Type == DatePickerType.dateTime || this.Type == DatePickerType.month) {
            if (this.selectedRangeValue) {
                if (this.Type === DatePickerType.dateTime) {
                    let time = this.form.controls.time.value?.split(':') ?? ['00', '00'];
                    this.selectedRangeValue?.setHours(+time[0], +time[1]);
                }
            }
            this.onChange(this.selectedRangeValue);
            this.onSelect.emit(this.selectedRangeValue);
        }

        // Fecha o menu do calendário
        this.isOpen = false;
    }

    /**
     * @description Devolve a data com a timezone aplicada
     *
     * Ex data: quando o utilizador escolhe uma data esta é guardada com a timezone local (10/10/2024 00:00 +0100) que depois é automaticamente convertida em (09/10/2024 23:00 UTC) então aplicamos o offset (10/10/2024 01:00 +0100 que converte para 10/10/2024 00:00 UTC) para termos o dia correto guardado
     * @param {Date} date Data a transformar
     * @return {Date} Data com a diferença de horas relativamente a timezone
     */
    offsetTimeZone(date: Date): Date {
        const offset = date.getTimezoneOffset() * 60 * 1000; // get the difference in milliseconds between the UTC time and local time
        let newDate = new Date(date.getTime() - offset);
        return newDate;
    }

    /**
     * @description Limpa as datas selecionadas
     * @return {void}
     */
    cleanDates(emitDateChange: boolean = true): void {
        if (this.separateInputs) {
            if (this.affectedDate == 'start') {
                this.form.controls.startDate.reset(null, { emitEvent: false });
                this.form.controls.startTime.reset(null, { emitEvent: false });
            } else if (this.affectedDate == 'end') {
                this.form.controls.endDate.reset(null, { emitEvent: false });
                this.form.controls.endTime.reset(null, { emitEvent: false });
            } else {
                // no side in particular (e.g. the form was reset from outside): clear both
                this.form.controls.startDate.reset(null, { emitEvent: false });
                this.form.controls.startTime.reset(null, { emitEvent: false });
                this.form.controls.endDate.reset(null, { emitEvent: false });
                this.form.controls.endTime.reset(null, { emitEvent: false });
            }
        } else {
            this.form.reset({}, { emitEvent: false });
        }


        if (this.Type == DatePickerType.dateRange || this.Type == DatePickerType.dateTimeRange) {
            if (this.affectedDate == 'start') {
                this.selectedRangeValue = new DateRange<Date>(null, this.selectedRangeValue.end);
            } else if (this.affectedDate == 'end') {
                this.selectedRangeValue = new DateRange<Date>(this.selectedRangeValue.start, null);
            } else {
                this.selectedRangeValue = new DateRange<Date>(null, null);
            }
        } else if (this.Type == DatePickerType.timeRange) {
            this.selectedRangeValue = { start: null, end: null };
        } else if (this.Type == DatePickerType.date || this.Type == DatePickerType.dateTime || this.Type == DatePickerType.time) {
            this.selectedRangeValue = null;
        }
        if (this.Type == DatePickerType.time || this.Type == DatePickerType.timeRange) {
            this.form.controls.time.setValue(this.getFormattedTimeFromDate(this.dateNow), { emitEvent: false });
            this.form.controls.startTime.setValue(this.getFormattedTimeFromDate(this.dateNow), { emitEvent: false });
            this.form.controls.endTime.setValue(this.getFormattedTimeFromDate(this.dateNow), { emitEvent: false });
        } else {
            this.form.controls.time.setValue(this.getFormattedTimeFromDate(this.dateNow), { emitEvent: false });
            this.form.controls.startTime.setValue(this.getFormattedTimeFromDate(this.dateNow), { emitEvent: false });
            this.form.controls.endTime.setValue(this.getFormattedTimeFromDate(this.dateNow), { emitEvent: false });
        }

        if (emitDateChange) {
            this.onChange(this.selectedRangeValue);
            this.onDateClean.emit();

            // Abre o menu do calendário
            if (this.Type != DatePickerType.time && this.Type != DatePickerType.timeRange) {
                this.isOpen = true;
            }
        }


    }

    /**
     * Cancela a seleção da data e restora o valor anterior da mesma
    */
    cancelDates() {
        if (this.selectedRangeValue != this.previousSelectedRangeValue) {
            this.selectedRangeValue = this.previousSelectedRangeValue;
            this.detectChanges(this.selectedRangeValue);
        }

    }

    /**
     * Guarda o valor das data selecionadas atualmente
     */
    onMenuOpen() {
        this.previousSelectedRangeValue = this.selectedRangeValue;
    }

    /**
     * Dá unsubscribe as subscrições feitas
     * @return {void}
     */
    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    /**
     * Processa a data recebida pelo componente pai e popula as variáveis necessárias
     * @param value Data recebida
     */
    detectChanges(value: any) {
        if (value || value?.start && value?.end) {

            if (this.Type != DatePickerType.time && this.Type != DatePickerType.timeRange) {
                // Valida se o valor enviado é uma Date ou um objeto equivalente a um DateRange
                if (Object.hasOwn(value, 'start') && value.start != null && Object.hasOwn(value, 'end') && value.end != null) {
                    // Converte os valores enviados para Date (se for string converte em Date mas se já for uma Date é recriada com os mesmos valores)
                    value.start = new Date(value.start);
                    value.end = new Date(value.end);
                }
                else {
                    value = new Date(value);
                }



                if (this.Type == DatePickerType.dateRange || this.Type == DatePickerType.dateTimeRange) {
                    this.selectedRangeValue = new DateRange<Date>(value.start, value.end);
                    this.form.controls.startTime.setValue(this.getFormattedTimeFromDate(value.start), { emitEvent: false });
                    this.form.controls.endTime.setValue(this.getFormattedTimeFromDate(value.end), { emitEvent: false });
                    this.form.controls.startDate.setValue(this.pipeDate(value.start));
                    this.form.controls.endDate.setValue(this.pipeDate(value.end));
                }

                if (this.Type == DatePickerType.date || this.Type == DatePickerType.dateTime) {
                    this.selectedRangeValue = value;
                    this.form.controls.time.setValue(this.getFormattedTimeFromDate(value), { emitEvent: false });
                    this.form.controls.date.setValue(this.pipeDate(value));
                }
            }
            else {
                if (this.Type == DatePickerType.time) {
                    this.selectedRangeValue = value;
                    this.form.controls.time.setValue(value, { emitEvent: false });
                }

                if (this.Type == DatePickerType.timeRange) {
                    this.selectedRangeValue = value;
                    this.form.controls.startTime.setValue(value.start, { emitEvent: false });
                    this.form.controls.endTime.setValue(value.end, { emitEvent: false });
                }
            }


        } else {
            // the value was emptied from outside (form reset): both sides must be cleared, not only the last one the user touched
            this.affectedDate = null;
            this.cleanDates(false);
        }
    }

    /**
     * Allows Angular to register a function to call when the model changes.
     * @param  {(value:any)=>void} fn
     * @returns void
     */
    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    //#region ControlValueAccessor
    /**
     * Allows Angular to update the model.
     * @param  {any} value - The value to update
     * @returns void
     */
    writeValue(value: any): void {
        this.previousSelectedRangeValue = value;
        this.detectChanges(value);
        this.onChange(value);
    }



    /**
     * Allows Angular to register a function to call when the input has been touched.
     * @param  {()=>void} fn
     * @returns void
     */
    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * Allows Angular to disable the input.
     * @param  {boolean} isDisabled
     * @returns void
     */
    setDisabledState(isDisabled: boolean): void {
        // this.disabled = isDisabled;
    }
    //#endregion

    /**
     * Valida as condições para o botão de confirmar estar desbloqueado
     */
    confirmButtonCheck() {
        // O botão fica disabled se ...
        switch (this.Type) {
            case DatePickerType.date: // ... Não tiver data selecionada e for obrigatório
                return this.selectedRangeValue == null && this.required;
            case DatePickerType.dateRange: // ... Não tiver data de inicio/fim selecionada e for obrigatório
                return (this.selectedRangeValue?.start == null || this.selectedRangeValue?.end == null) && this.required;
            case DatePickerType.dateTime: // ... Não tiver data ou hora selecionada e for obrigatório
                return (this.selectedRangeValue == null || this.form.controls.time.value == null) && this.required;
            case DatePickerType.dateTimeRange: // ... Não tiver data/hora de inicio/fim selecionada e for obrigatório
                return (this.selectedRangeValue?.start == null || this.selectedRangeValue?.end == null || this.form.controls.startTime.value == null || this.form.controls.endTime.value == null) && this.required;
            default:
                return true;
        }
    }

    /**
     * Aplica o DatePipe a uma data, se a data vier a null é devolvido null
     */
    pipeDate(newDate: Date | null, monthFormat: boolean = false): string | null {
        if (newDate) {
            return this._datePipe.transform(newDate, monthFormat ? 'MMMM YYYY' : this.dateFormat);
        }
        return null;
    }

    /**
     * Extrai a string de horas e minutos de uma data adicionando os zeros caso o número seja menor de 10
     * @param date Data de onde extrair as horas
     * @returns A string com os minutos e as horas bem formatados
     */
    getFormattedTimeFromDate(date: Date | null): string {
        let hours: string | null = null;
        let minutes: string | null = null;

        if (date) {
            hours = date.getHours() >= 10 ? date.getHours().toString() : '0' + date.getHours().toString();
            minutes = date.getMinutes() >= 10 ? date.getMinutes().toString() : '0' + date.getMinutes().toString();
        }

        return hours + ':' + minutes;
    }

    changeAffectedDate(newAffectedDate: 'start' | 'end') {
        this.affectedDate = newAffectedDate;
    }

    /**
     * Seleciona o intervalo de datas baseado na opção recebida
     * @param preset Opção de intervalo
     */
    handlePresetDates(preset: PresetDateRanges) {
        let resultDataRange: DateRange<Date>;
        let auxDate: Date = new Date();

        switch (preset) {
            case PresetDateRanges.lastWeek: {
                let firstDayOfWeek = auxDate.getDate() - 7 - auxDate.getDay();
                let dateStart = new Date(auxDate.getFullYear(), auxDate.getMonth(), firstDayOfWeek);
                let dateEnd = new Date(auxDate.getFullYear(), auxDate.getMonth(), firstDayOfWeek + 7);

                if (this.Type == DatePickerType.dateTimeRange) {
                    dateStart.setHours(0, 0, 0, 0);
                    dateEnd.setHours(23, 59, 59, 0);
                }

                resultDataRange = new DateRange<Date>(dateStart, dateEnd);
                break;
            }
            case PresetDateRanges.currentWeek: {
                let firstDayOfWeek = auxDate.getDate() - auxDate.getDay();
                let dateStart = new Date(auxDate.getFullYear(), auxDate.getMonth(), firstDayOfWeek);
                let dateEnd = new Date(auxDate.getFullYear(), auxDate.getMonth(), firstDayOfWeek + 7);

                if (this.Type == DatePickerType.dateTimeRange) {
                    dateStart.setHours(0, 0, 0, 0);
                    dateEnd.setHours(23, 59, 59, 0);
                }

                resultDataRange = new DateRange<Date>(dateStart, dateEnd);
                break;
            }
            case PresetDateRanges.nextWeek: {
                let firstDayOfWeek = auxDate.getDate() + 7 - auxDate.getDay();
                let dateStart = new Date(auxDate.getFullYear(), auxDate.getMonth(), firstDayOfWeek);
                let dateEnd = new Date(auxDate.getFullYear(), auxDate.getMonth(), firstDayOfWeek + 7);

                if (this.Type == DatePickerType.dateTimeRange) {
                    dateStart.setHours(0, 0, 0, 0);
                    dateEnd.setHours(23, 59, 59, 0);
                }

                resultDataRange = new DateRange<Date>(dateStart, dateEnd);
                break;
            }
            case PresetDateRanges.lastMonth: {
                let dateStart = new Date(auxDate.getFullYear(), auxDate.getMonth() - 1, 1);
                let dateEnd = new Date(auxDate.getFullYear(), auxDate.getMonth(), 0); // Dia 0 do mês é interpretado como o último dia do mês passado

                if (this.Type == DatePickerType.dateTimeRange) {
                    dateStart.setHours(0, 0, 0, 0);
                    dateEnd.setHours(23, 59, 59, 0);
                }

                resultDataRange = new DateRange<Date>(dateStart, dateEnd);
                break;
            }
            case PresetDateRanges.currentMonth: {
                let dateStart = new Date(auxDate.getFullYear(), auxDate.getMonth(), 1);
                let dateEnd = new Date(auxDate.getFullYear(), auxDate.getMonth() + 1, 0); // Dia 0 do mês é interpretado como o último dia do mês anterior

                if (this.Type == DatePickerType.dateTimeRange) {
                    dateStart.setHours(0, 0, 0, 0);
                    dateEnd.setHours(23, 59, 59, 0);
                }

                resultDataRange = new DateRange<Date>(dateStart, dateEnd);
                break;
            }
            case PresetDateRanges.nextMonth: {
                let dateStart = new Date(auxDate.getFullYear(), auxDate.getMonth() + 1, 1);
                let dateEnd = new Date(auxDate.getFullYear(), auxDate.getMonth() + 2, 0); // Dia 0 do mês é interpretado como o último dia do mês anterior

                if (this.Type == DatePickerType.dateTimeRange) {
                    dateStart.setHours(0, 0, 0, 0);
                    dateEnd.setHours(23, 59, 59, 0);
                }

                resultDataRange = new DateRange<Date>(dateStart, dateEnd);
                break;
            }
            case PresetDateRanges.monthUntilNow: {
                let dateStart = new Date(auxDate.getFullYear(), auxDate.getMonth(), 1);
                let dateEnd = new Date(auxDate.getFullYear(), auxDate.getMonth(), auxDate.getDate());

                if (this.Type == DatePickerType.dateTimeRange) {
                    dateStart.setHours(0, 0, 0, 0);
                    dateEnd.setHours(23, 59, 59, 0);
                }

                resultDataRange = new DateRange<Date>(dateStart, dateEnd);
                break;
            }
            case PresetDateRanges.nowUntilEndOfMonth: {
                let dateStart = new Date(auxDate.getFullYear(), auxDate.getMonth(), auxDate.getDate());
                let dateEnd = new Date(auxDate.getFullYear(), auxDate.getMonth() + 1, 0);

                if (this.Type == DatePickerType.dateTimeRange) {
                    dateStart.setHours(0, 0, 0, 0);
                    dateEnd.setHours(23, 59, 59, 0);
                }

                resultDataRange = new DateRange<Date>(dateStart, dateEnd);
                break;
            }
            case PresetDateRanges.lastYear: {
                let dateStart = new Date(auxDate.getFullYear() - 1, 0, 1);
                let dateEnd = new Date(auxDate.getFullYear(), 0, 0); // Dia 0 do ano é interpretado como o último dia do ano anterior

                if (this.Type == DatePickerType.dateTimeRange) {
                    dateStart.setHours(0, 0, 0, 0);
                    dateEnd.setHours(23, 59, 59, 0);;
                }

                resultDataRange = new DateRange<Date>(dateStart, dateEnd);
                break;
            }
            case PresetDateRanges.currentYear: {
                let dateStart = new Date(auxDate.getFullYear(), 0, 1);
                let dateEnd = new Date(auxDate.getFullYear() + 1, 0, 0); // Dia 0 do ano é interpretado como o último dia do ano anterior

                if (this.Type == DatePickerType.dateTimeRange) {
                    dateStart.setHours(0, 0, 0, 0);
                    dateEnd.setHours(23, 59, 59, 0);;
                }

                resultDataRange = new DateRange<Date>(dateStart, dateEnd);
                break;
            }
            case PresetDateRanges.nextYear: {
                let dateStart = new Date(auxDate.getFullYear() + 1, 0, 1);
                let dateEnd = new Date(auxDate.getFullYear() + 2, 0, 0); // Dia 0 do ano é interpretado como o último dia do ano anterior

                if (this.Type == DatePickerType.dateTimeRange) {
                    dateStart.setHours(0, 0, 0, 0);
                    dateEnd.setHours(23, 59, 59, 0);;
                }

                resultDataRange = new DateRange<Date>(dateStart, dateEnd);
                break;
            }
            case PresetDateRanges.yearUntilNow: {
                let dateStart = new Date(auxDate.getFullYear(), 0, 1);
                let dateEnd = new Date(auxDate.getFullYear(), auxDate.getMonth(), auxDate.getDate());

                if (this.Type == DatePickerType.dateTimeRange) {
                    dateStart.setHours(0, 0, 0, 0);
                    dateEnd.setHours(23, 59, 59, 0);
                }

                resultDataRange = new DateRange<Date>(dateStart, dateEnd);
                break;
            }
            case PresetDateRanges.nowUntilEndOfYear: {
                let dateStart = new Date(auxDate.getFullYear(), auxDate.getMonth(), auxDate.getDate());
                let dateEnd = new Date(auxDate.getFullYear() + 1, 0, 0);

                if (this.Type == DatePickerType.dateTimeRange) {
                    dateStart.setHours(0, 0, 0, 0);
                    dateEnd.setHours(23, 59, 59, 0);
                }

                resultDataRange = new DateRange<Date>(dateStart, dateEnd);
                break;
            }
            case PresetDateRanges.lastSevenDays: {
                let dateStart = new Date(auxDate.getFullYear(), auxDate.getMonth(), auxDate.getDate() - 7);
                let dateEnd = new Date(auxDate.getFullYear(), auxDate.getMonth(), auxDate.getDate());

                if (this.Type == DatePickerType.dateTimeRange) {
                    dateStart.setHours(0, 0, 0, 0);
                    dateEnd.setHours(23, 59, 59, 0);
                }

                resultDataRange = new DateRange<Date>(dateStart, dateEnd);
                break;
            }
            case PresetDateRanges.lastThirtyDays: {
                let dateStart = new Date(auxDate.getFullYear(), auxDate.getMonth(), auxDate.getDate() - 30);
                let dateEnd = new Date(auxDate.getFullYear(), auxDate.getMonth(), auxDate.getDate());

                if (this.Type == DatePickerType.dateTimeRange) {
                    dateStart.setHours(0, 0, 0, 0);
                    dateEnd.setHours(23, 59, 59, 0);
                }

                resultDataRange = new DateRange<Date>(dateStart, dateEnd);
                break;
            }
            default:
                resultDataRange = new DateRange<Date>(null, null);
                break;
        }
        this.detectChanges(resultDataRange);
        this.select();
    }

    onDatepickerClick() {
        this.isOpen = !this.isOpen;
        // this.onTouched()
    }

    onBlur() {
        this.onTouched();
    }
}


export class ISDateTimePickerDatasource {
    constructor(
        public Type: DatePickerType = DatePickerType.dateRange,
        public selectedDates?: Date | { start: Date, end: Date; }
    ) { }
}

export enum DatePickerType {
    date = 'date',
    time = 'time',
    dateTime = 'dateTime',
    dateRange = 'dateRange',
    timeRange = 'timeRange',
    dateTimeRange = 'dateTimeRange',
    month = 'month',
    none = 'none'
}

export enum PresetDateRanges {
    lastWeek,
    currentWeek,
    nextWeek,
    lastMonth,
    currentMonth,
    nextMonth,
    monthUntilNow,
    nowUntilEndOfMonth,
    lastYear,
    currentYear,
    nextYear,
    yearUntilNow,
    nowUntilEndOfYear,
    lastSevenDays,
    lastThirtyDays
}